import * as Clipboard from 'expo-clipboard';
import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Pressable, ScrollView, Share, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import QRCode from 'react-native-qrcode-svg';

import {
  collectReferralVoucherApi,
  fetchReferralCasesApi,
  fetchReferralInfoApi,
  ReferralCase,
  ReferralInfo,
} from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { StatusChip } from '@/components/ui/status-chip';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, spacing } from '@/theme/tokens';

// Public install link — used when a guest (no account, so no personal code)
// wants to pass the app along anyway.
const APP_URL = 'https://opiekun-rodziny.expo.app';

/**
 * "Poleć aplikację" — mock: polecenia_nowe_kolekcje. Consumer 50 zł voucher
 * program: share MY code, never type a friend's phone number (their data,
 * their consent — RODO). The friend enters the code on the login screen;
 * vouchers are paid out manually by the office after verifying the condition.
 */
export default function ReferScreen() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);

  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [copied, setCopied] = useState(false);

  // "Moje polecenia": per-referral statuses + digital voucher pickup. The list
  // is the trust anchor of the program — the referrer must SEE it working.
  const [cases, setCases] = useState<ReferralCase[]>([]);
  const [collectingId, setCollectingId] = useState<number | null>(null);
  const [pickupFirstName, setPickupFirstName] = useState('');
  const [pickupLastName, setPickupLastName] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupSaving, setPickupSaving] = useState(false);
  const [pickupError, setPickupError] = useState<string | null>(null);
  const [codeCopiedId, setCodeCopiedId] = useState<number | null>(null);

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    (async () => {
      const res = await fetchReferralInfoApi();
      if (!cancelled && res.ok && res.data) setInfo(res.data);
      const casesRes = await fetchReferralCasesApi();
      if (!cancelled && casesRes.ok && casesRes.data) setCases(casesRes.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [account]);

  const confirmPickup = async (caseId: number) => {
    if (!pickupFirstName.trim() || !pickupLastName.trim() || pickupSaving) return;
    setPickupSaving(true);
    setPickupError(null);
    const res = await collectReferralVoucherApi(caseId, {
      firstName: pickupFirstName.trim(),
      lastName: pickupLastName.trim(),
      address: pickupAddress.trim() || undefined,
    });
    setPickupSaving(false);
    if (res.ok && res.data) {
      setCases((prev) => prev.map((c) => (c.id === caseId ? res.data! : c)));
      setCollectingId(null);
      setPickupFirstName('');
      setPickupLastName('');
      setPickupAddress('');
    } else {
      setPickupError(res.message || t('refer.cases.collectError'));
    }
  };

  const copyVoucherCode = async (item: ReferralCase) => {
    if (!item.voucherCode) return;
    await Clipboard.setStringAsync(item.voucherCode);
    setCodeCopiedId(item.id);
    setTimeout(() => setCodeCopiedId(null), 2000);
  };

  const CASE_CHIP: Record<
    ReferralCase['status'],
    { kind: 'success' | 'warning' | 'error' | 'info'; icon: keyof typeof Ionicons.glyphMap }
  > = {
    SUBMITTED: { kind: 'info', icon: 'hourglass-outline' },
    PURCHASED: { kind: 'info', icon: 'shield-checkmark-outline' },
    VOUCHER_GRANTED: { kind: 'warning', icon: 'gift-outline' },
    VOUCHER_COLLECTED: { kind: 'success', icon: 'checkmark-circle-outline' },
    REJECTED: { kind: 'error', icon: 'close-circle-outline' },
  };

  const shareText = info
    ? t('refer.shareText', { url: info.shareUrl, code: info.code })
    : '';

  const onSms = () => {
    void Linking.openURL(`sms:?body=${encodeURIComponent(shareText)}`);
  };

  const shareMessage = async (message: string) => {
    try {
      await Share.share({ message });
    } catch {
      // RN Share unavailable -> try the browser's native share sheet (mobile
      // web has it: WhatsApp/Messenger/SMS), then fall back to copying.
      const nav = globalThis.navigator as (Navigator & { share?: (data: { text: string }) => Promise<void> }) | undefined;
      if (nav?.share) {
        try {
          await nav.share({ text: message });
          return;
        } catch {
          // user cancelled or share failed — copy the text instead
        }
      }
      await Clipboard.setStringAsync(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const onShare = () => void shareMessage(shareText);

  // Guest path: no personal code yet, but the user can still pass the app on —
  // the message explains the 50 zł fuel voucher requires logging in + the friend
  // buying a policy (owner: "polecenie bez konta, wyślij link znajomym").
  const onGuestShare = () => void shareMessage(t('refer.guestShareText', { url: APP_URL }));

  const copyCode = async () => {
    if (!info) return;
    await Clipboard.setStringAsync(info.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('refer.title')} />

        {/* Gift hero */}
        <View style={{ alignItems: 'center', gap: spacing.sm }}>
          <View
            style={{
              width: 104,
              height: 104,
              borderRadius: radius.full,
              backgroundColor: colors.card,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons name="gift-outline" size={52} color={colors.navy} />
          </View>
          <AppText variant="headlineMd" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('refer.heroTitle')}
          </AppText>
        </View>

        {account ? (
          <>
            {/* Bonus terms — 50 zł for BOTH sides + the "new client" condition */}
            <Card style={{ gap: spacing.sm }}>
              <View style={{ backgroundColor: colors.infoBg, borderRadius: radius.chip, padding: spacing.md, gap: spacing.xs }}>
                <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
                  {t('refer.bonusBody')}
                </AppText>
                <AppText variant="labelMd" style={{ textAlign: 'center' }}>
                  {t('refer.bonusCondition')}
                </AppText>
              </View>
              <PrimaryButton label={t('refer.sendSms')} onPress={onSms} disabled={!info} />
              <PrimaryButton label={t('refer.share')} variant="secondary" onPress={onShare} disabled={!info} />
            </Card>

            {/* My code + copy */}
            <Pressable accessibilityRole="button" onPress={() => void copyCode()} disabled={!info}>
              <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
                <View style={{ flex: 1 }}>
                  <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                    {t('refer.yourCode')}
                  </AppText>
                  <AppText variant="headlineMd" color={colors.primary}>
                    {info?.code ?? '…'}
                  </AppText>
                </View>
                <Ionicons
                  name={copied ? 'checkmark-outline' : 'copy-outline'}
                  size={24}
                  color={copied ? colors.success : colors.primary}
                />
              </Card>
            </Pressable>
            {copied && (
              <AppText variant="labelMd" color={colors.success} style={{ textAlign: 'center' }}>
                {t('refer.copied')}
              </AppText>
            )}

            {/* QR (Napper gift-screen pattern): friend scans -> install link with code */}
            {info && (
              <Card style={{ alignItems: 'center', gap: spacing.sm }}>
                <QRCode
                  value={`${info.shareUrl}${info.shareUrl.includes('?') ? '&' : '?'}ref=${info.code}`}
                  size={168}
                  color={colors.navy}
                  backgroundColor="#FFFFFF"
                />
                <AppText variant="labelMd" style={{ textAlign: 'center' }}>
                  {t('refer.qrHint')}
                </AppText>
              </Card>
            )}

            {/* How the friend uses it + payout note */}
            <View style={{ flexDirection: 'row', gap: spacing.xs, paddingHorizontal: spacing.xs }}>
              <Ionicons name="information-circle-outline" size={18} color={colors.primary} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>
                {t('refer.howItWorks')}
              </AppText>
            </View>

            {/* Moje polecenia — status per referral + digital voucher pickup */}
            <AppText variant="headlineMd" color={colors.navy} style={{ marginTop: spacing.sm }}>
              {t('refer.cases.title')}
            </AppText>
            {cases.length === 0 ? (
              <Card>
                <AppText variant="bodyMd" color={colors.textSecondary}>
                  {t('refer.cases.empty')}
                </AppText>
              </Card>
            ) : (
              cases.map((item) => (
                <Card key={item.id} style={{ gap: spacing.sm }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
                    <View style={{ flex: 1, minWidth: 120 }}>
                      <AppText variant="bodyLg">
                        {item.display || t('refer.cases.unknownPerson')}
                      </AppText>
                      {item.createdAt ? (
                        <AppText variant="labelMd" color={colors.textSecondary}>
                          {new Date(item.createdAt).toLocaleDateString('pl-PL')}
                        </AppText>
                      ) : null}
                    </View>
                    <StatusChip
                      kind={CASE_CHIP[item.status]?.kind ?? 'info'}
                      icon={CASE_CHIP[item.status]?.icon}
                      label={t(`refer.cases.statuses.${item.status}`, item.status)}
                    />
                  </View>

                  {item.status === 'VOUCHER_GRANTED' &&
                    (collectingId === item.id ? (
                      <View style={{ gap: spacing.sm }}>
                        <AppText variant="bodyMd" color={colors.textSecondary}>
                          {t('refer.cases.collectInfo')}
                        </AppText>
                        <TextField
                          label={t('refer.cases.firstName')}
                          value={pickupFirstName}
                          onChangeText={setPickupFirstName}
                          autoCapitalize="words"
                        />
                        <TextField
                          label={t('refer.cases.lastName')}
                          value={pickupLastName}
                          onChangeText={setPickupLastName}
                          autoCapitalize="words"
                        />
                        <TextField
                          label={t('refer.cases.address')}
                          value={pickupAddress}
                          onChangeText={setPickupAddress}
                        />
                        {pickupError ? (
                          <AppText variant="labelMd" color={colors.error}>
                            {pickupError}
                          </AppText>
                        ) : null}
                        <PrimaryButton
                          label={t('refer.cases.confirmCollect')}
                          onPress={() => void confirmPickup(item.id)}
                          disabled={pickupSaving || !pickupFirstName.trim() || !pickupLastName.trim()}
                        />
                      </View>
                    ) : (
                      <PrimaryButton
                        label={t('refer.cases.collectCta')}
                        onPress={() => {
                          setCollectingId(item.id);
                          setPickupError(null);
                        }}
                      />
                    ))}

                  {item.status === 'VOUCHER_COLLECTED' && item.voucherCode ? (
                    <Pressable accessibilityRole="button" onPress={() => void copyVoucherCode(item)}>
                      <View
                        style={{
                          backgroundColor: colors.infoBg,
                          borderRadius: radius.chip,
                          padding: spacing.md,
                          gap: spacing.xs,
                        }}
                      >
                        <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                          {t('refer.cases.voucherCode', { amount: item.amountPln ?? 50 })}
                        </AppText>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                          <AppText variant="headlineMd" color={colors.primary} style={{ flex: 1 }}>
                            {item.voucherCode}
                          </AppText>
                          <Ionicons
                            name={codeCopiedId === item.id ? 'checkmark-outline' : 'copy-outline'}
                            size={22}
                            color={codeCopiedId === item.id ? colors.success : colors.primary}
                          />
                        </View>
                        <AppText variant="labelMd" color={colors.textSecondary}>
                          {codeCopiedId === item.id
                            ? t('refer.cases.codeCopied')
                            : t('refer.cases.voucherHint')}
                        </AppText>
                      </View>
                    </Pressable>
                  ) : null}
                </Card>
              ))
            )}
          </>
        ) : (
          /* Guest: no personal code yet, but they can still share the app link.
             The 50 zł voucher needs an account + the friend buying a policy. */
          <Card style={{ gap: spacing.sm }}>
            <View style={{ backgroundColor: colors.infoBg, borderRadius: radius.chip, padding: spacing.md }}>
              <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
                {t('refer.guestSharePitch')}
              </AppText>
            </View>
            <PrimaryButton label={t('refer.guestShare')} onPress={onGuestShare} />
            {copied && (
              <AppText variant="labelMd" color={colors.success} style={{ textAlign: 'center' }}>
                {t('refer.linkCopied')}
              </AppText>
            )}
            <View style={{ flexDirection: 'row', gap: spacing.xs, paddingHorizontal: spacing.xs }}>
              <Ionicons name="gift-outline" size={18} color={colors.primary} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>
                {t('refer.guestVoucherNote')}
              </AppText>
            </View>
            <PrimaryButton
              label={t('refer.guestLoginCta')}
              variant="secondary"
              onPress={() => router.push('/auth/login')}
            />
          </Card>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
